import React, { useState, useMemo, ComponentProps } from 'react';
import classnames from 'classnames';
import { triggerSignUp } from '@fiverr-private/identification_triggers';
import { Button } from '@fiverr-private/fit';
import pathfinder from '@fiverr-private/pathfinder';
import { getContext } from '@fiverr-private/fiverr_context';
import { calculateGroup, clientAllocate } from '@fiverr-private/experiments';
import withTranslation from '../../lib/withTranslation';
import { sendBigQueryEvent } from '../../utils/bigQuery';
import { EXPERIMENTS, getVariant, VARIANTS } from '../../utils/experiments';
import { BIGQUERY_EVENT } from './constants';
import './style.scss';

interface JoinLinkProps {
    fill?: ComponentProps<typeof Button>['fill'];
    color?: ComponentProps<typeof Button>['color'];
    size?: ComponentProps<typeof Button>['size'];
    textColor?: ComponentProps<typeof Button>['textColor'];
    i18n: any;
    isThemed?: boolean;
    inLynxesSeamlessOnboardingAndRoutingRollout?: boolean;
    isEligibleForOnboardingExperience?: boolean;
}

const JoinLink = ({
    fill = (Button as any).FILLS.GHOST,
    color = (Button as any).TEXT_COLORS.GREEN,
    size = (Button as any).SIZES.SMALL,
    textColor,
    i18n,
    isThemed = false,
    inLynxesSeamlessOnboardingAndRoutingRollout = false,
    isEligibleForOnboardingExperience = false,
}: JoinLinkProps) => {
    const [loading, setLoading] = useState(false);

    const shouldLeadToOnboarding = useMemo(() => {
        if (!isEligibleForOnboardingExperience) {
            return false;
        }

        if (getVariant(EXPERIMENTS.LYNXES_SEAMLESS_ONBOARDING_AND_ROUTING) === VARIANTS.B) {
            return true;
        }

        if (!inLynxesSeamlessOnboardingAndRoutingRollout) {
            return false;
        }

        const calculatedGroup = calculateGroup({
            experimentId: EXPERIMENTS.LYNXES_SEAMLESS_ONBOARDING_AND_ROUTING,
            experimentType: 'guest',
            numOfGroups: 2,
            context: getContext(),
        });

        return calculatedGroup === VARIANTS.B;
    }, [inLynxesSeamlessOnboardingAndRoutingRollout, isEligibleForOnboardingExperience]);

    const triggerClassName = classnames('fiverr-header-join', { 'themed-link': isThemed });

    const path = pathfinder(
        'join',
        {},
        {
            query: {
                source: 'top_nav',
            },
        }
    );

    const onboardingPath = pathfinder(
        'signup_page',
        {},
        {
            query: {
                source: 'top_nav',
            },
        }
    );

    const handleOnboardingAllocation = () => {
        if (!isEligibleForOnboardingExperience || !inLynxesSeamlessOnboardingAndRoutingRollout) {
            return;
        }

        clientAllocate({
            experimentId: EXPERIMENTS.LYNXES_SEAMLESS_ONBOARDING_AND_ROUTING,
            experimentType: 'guest',
            numOfGroups: 2,
            rolloutResult: inLynxesSeamlessOnboardingAndRoutingRollout,
        });
    };

    const handleClick = async (event) => {
        handleOnboardingAllocation();
        if (shouldLeadToOnboarding) {
            return;
        }

        try {
            event.preventDefault();
            setLoading(true);
            await triggerSignUp({ modalOptions: { source: 'header_join_link' } });
            sendBigQueryEvent(BIGQUERY_EVENT);
        } catch {
            window.location.assign(path);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Button
            href={shouldLeadToOnboarding ? onboardingPath : path}
            loading={loading}
            rel="nofollow"
            color={color}
            textColor={textColor}
            fill={fill}
            size={size}
            className={triggerClassName}
            onClick={handleClick}
        >
            {i18n.t('header.join')}
        </Button>
    );
};

export default withTranslation(JoinLink);
